/**
 * Typed API client for the expert-insights Edge Function.
 * Auth is handled by expertInsightsAPIClient — no token threading at call sites.
 */

import { expertInsightsAPIClient } from "./apiClients";
import { appendOffsetParams } from "./pagination";
import type {
  ContributorProfile,
  UpdateProfileBody,
  ExpertInsightSummary,
  InsightListPage,
  CreateInsightBody,
  UpdateDraftBody,
} from "@shared/types/expert_insights/api";
import type { TaxonomyNode, TaxonomyNodesResponse } from "@shared/types/taxonomy.ts";
import type { OffsetBasedPage } from "@shared/types/pagination.ts";
export {
  INSIGHT_TITLE_MAX_WORDS,
  INSIGHT_BODY_MAX_WORDS,
  INSIGHT_LIST_MAX_LIMIT,
} from "@shared/types/expert_insights/api";

export type {
  ContributorProfile,
  UpdateProfileBody,
  ExpertInsightSummary,
  InsightListPage,
  CreateInsightBody,
  UpdateDraftBody,
};

export interface InsightListParams extends OffsetBasedPage {
  q?: string;
  field?: string;
}

export async function getOwnProfile(): Promise<ContributorProfile> {
  const { data } = await expertInsightsAPIClient.get<ContributorProfile>("/profile");
  return data;
}

export async function updateOwnProfile(body: UpdateProfileBody): Promise<ContributorProfile> {
  const { data } = await expertInsightsAPIClient.patch<ContributorProfile>("/profile", body);
  return data;
}

export async function uploadAvatar(file: File): Promise<ContributorProfile> {
  const form = new FormData();
  form.append("avatar", file);
  // Delete the instance-level application/json default so the browser sets Content-Type
  // to multipart/form-data with the correct boundary.
  const { data } = await expertInsightsAPIClient.post<ContributorProfile>("/profile/avatar", form, {
    headers: { "Content-Type": undefined },
  });
  return data;
}

export async function listInsights(params: InsightListParams = {}): Promise<InsightListPage> {
  const qs = new URLSearchParams();
  if (params.q) qs.set("q", params.q);
  if (params.field) qs.set("field", params.field);
  appendOffsetParams(qs, params);
  const suffix = qs.toString() ? `?${qs.toString()}` : "";
  const { data } = await expertInsightsAPIClient.get<InsightListPage>(`/insights${suffix}`);
  return data;
}

export async function getInsight(id: string): Promise<ExpertInsightSummary> {
  const { data } = await expertInsightsAPIClient.get<ExpertInsightSummary>(`/insights/${id}`);
  return data;
}

export async function createInsight(body: CreateInsightBody): Promise<ExpertInsightSummary> {
  const { data } = await expertInsightsAPIClient.post<ExpertInsightSummary>("/insights", body);
  return data;
}

export async function updateInsight(
  id: string,
  body: UpdateDraftBody
): Promise<ExpertInsightSummary> {
  const { data } = await expertInsightsAPIClient.patch<ExpertInsightSummary>(
    `/insights/${id}`,
    body
  );
  return data;
}

export async function deleteInsight(id: string): Promise<void> {
  await expertInsightsAPIClient.delete(`/insights/${id}`);
}

export async function publishInsight(id: string): Promise<void> {
  await expertInsightsAPIClient.post(`/insights/${id}/publish`);
}

export async function withdrawInsight(id: string): Promise<void> {
  await expertInsightsAPIClient.post(`/insights/${id}/withdraw`);
}

/** The subject skeleton (domains and fields); open to any signed-in user. */
export async function getTaxonomyNodes(): Promise<TaxonomyNode[]> {
  const { data } = await expertInsightsAPIClient.get<TaxonomyNodesResponse>("/taxonomy-nodes");
  return data.nodes;
}
